import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import Sidemodal from "../../common/sidemodal/sidemodal";
import { useCapaAccess } from "./capaAccess";
import { fetchCapaDashboard } from "./capaService";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "./CapaDashboard.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
);

const stageOrder = [
  "Open",
  "Under Investigation",
  "Under RCA",
  "Action Planning",
  "Approved",
  "Implementation",
  "Verification",
  "Closure",
  "Closed",
  "Removed",
];

const filters = {
  dateRange: ["Last 7 Days", "Last 30 Days", "This Quarter"],
  status: ["All Status", "Overdue", "Upcoming", "Completed"],
  assignee: ["All Assignees", "My Tasks", "QA Team"],
};

const DASHBOARD_FILTER_INITIAL = {
  dateRange: "Last 30 Days",
  statuses: [],
  assignees: [],
};

function statusVariant(value) {
  const key = String(value || "").toLowerCase();
  if (key.includes("complete")) return "completed";
  if (key.includes("overdue")) return "overdue";
  return "upcoming";
}

function formatDate(raw) {
  if (!raw) return "—";
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString();
}

function resolveStageCounts(data) {
  const source = data?.stageCounts || {};
  const aliasMap = {
    "under investigation": ["investigation", "under investigation"],
    "under rca": ["rca", "under rca"],
    closed: ["closed", "closure"],
    closure: ["closure"],
    removed: ["removed"],
  };

  return stageOrder.map((stage) => {
    const stageKey = stage.toLowerCase();
    const aliases = aliasMap[stageKey] || [stageKey];
    const count =
      Object.entries(source).find(([key]) => aliases.includes(String(key).toLowerCase()))?.[1] ?? 0;
    return { stage, count: Number(count) || 0 };
  });
}

function DashboardHeader({ onRefresh, onOpenFilter, onClearFilters, activeFilterCount }) {
  return (
    <>
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA Dashboard" },
        ]}
        toolbarExtras={
          <div className="d-inline-block position-relative">
            <button
              type="button"
              className="actions-trigger capa-list-filter-trigger"
              onClick={onOpenFilter}
            >
              <i className="fas fa-filter me-1" aria-hidden />
              Filter
            </button>
            {activeFilterCount > 0 ? (
              <span
                role="button"
                tabIndex={0}
                className="capa-list-filter-trigger__badge position-absolute top-0 end-0"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onClearFilters();
                  }
                }}
                title="Clear filters"
                aria-label="Clear filters"
                style={{
                  zIndex: 10,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  top: "-0.35rem",
                  right: "-0.35rem",
                }}
              >
                {activeFilterCount} ×
              </span>
            ) : null}
          </div>
        }
        actions={[{ icon: "fas fa-sync-alt", label: "Refresh Data", onClick: onRefresh }]}
      />
    </>
  );
}

function KpiCard({ icon, label, value, tone, subtext }) {
  return (
    <div className={`capa-premium__kpi-card tone-${tone}`}>
      <div className="capa-premium__kpi-icon">
        <i className={icon} />
      </div>
      <div className="capa-premium__kpi-value">{value}</div>
      <div className="capa-premium__kpi-label">{label}</div>
      {subtext ? <div className="capa-premium__kpi-subtext">{subtext}</div> : null}
    </div>
  );
}

function ChartsSection({ donutData, trend }) {
  const lineData = {
    labels: trend.labels,
    datasets: [
      {
        label: "Created",
        data: trend.created,
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 18,
        fill: false,
        borderWidth: 2,
      },
      {
        label: "Closed",
        data: trend.closed,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 18,
        fill: false,
        borderWidth: 2,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8 },
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      x: { grid: { color: "#f1f5f9" }, ticks: { maxTicksLimit: 10, color: "#6b7280" } },
      y: { grid: { color: "#f1f5f9" }, ticks: { color: "#6b7280" }, beginAtZero: true },
    },
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, pointStyle: "circle" } },
    },
  };

  return (
    <div className="row g-3 mt-1">
      <div className="col-xl-8">
        <div className="capa-premium__card">
          <div className="capa-premium__card-head">CAPA Created vs Closed (30 Days)</div>
          <div className="capa-premium__chart-wrap">
            <Line options={lineOptions} data={lineData} />
          </div>
        </div>
      </div>
      <div className="col-xl-4">
        <div className="capa-premium__card">
          <div className="capa-premium__card-head">Overdue vs On-Time</div>
          <div className="capa-premium__donut-wrap">
            <Doughnut
              options={donutOptions}
              data={{
                labels: ["Overdue", "On-Time"],
                datasets: [{ data: donutData, backgroundColor: ["#ef4444", "#22c55e"], borderWidth: 0 }],
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskTable({ rows, navigate }) {
  return (
    <div className="capa-premium__card capa-premium__main-card">
      <div className="capa-premium__card-head">Task Table</div>
      <div className="table-responsive capa-premium__table-wrap capa-premium__main-card-body">
        <table className="table mb-0 capa-premium__task-table">
          <thead>
            <tr>
              <th>CAPA ID</th>
              <th>Assignee</th>
              <th>Task Details</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Type</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const assigneeName = String(row.assigneeName || "").trim() || "—";
              const status = statusVariant(row.dueSchedule);
              return (
                <tr key={row.id}>
                  <td className="fw-semibold">{row.capaNo || "—"}</td>
                  <td>
                    <div className="capa-premium__assignee" title={assigneeName}>
                      <span style={{ backgroundColor: getAvatarColor(assigneeName) }}>
                        {getInitials(assigneeName)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="capa-premium__ellipsis" title={row.taskDetails || ""}>
                      {row.taskDetails || "—"}
                    </div>
                  </td>
                  <td>{formatDate(row.overdueDate)}</td>
                  <td>
                    <span className={`badge rounded-pill capa-premium__status is-${status}`}>
                      {status === "completed" ? "Completed" : status === "overdue" ? "Overdue" : "Upcoming"}
                    </span>
                  </td>
                  <td>{row.type || "—"}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/capa/${row.capaId}`)} disabled={!row.capaId}>
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StagePanel({ stages }) {
  return (
    <div className="capa-premium__card capa-premium__main-card">
      <div className="capa-premium__card-head">CAPA by Stage</div>
      <div className="capa-premium__stage-list capa-premium__main-card-body">
        {stages.map((item) => {
          const isRemoved = String(item.stage).toLowerCase() === "removed";
          return (
            <button
              key={item.stage}
              type="button"
              className={`capa-premium__stage-item ${isRemoved ? "is-removed" : ""}`}
            >
              <span>{item.stage}</span>
              <span className="badge rounded-pill">{item.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BottomPanels({ myTasks }) {
  return (
    <div className="row g-3">
      <div className="col-lg-4">
        <div className="capa-premium__card capa-premium__compact-card">
          <div className="capa-premium__card-head">My Tasks</div>
          <div className="capa-premium__task-grid">
            <div className="is-pending">
              <span>Pending</span>
              <strong>{myTasks.pending}</strong>
            </div>
            <div className="is-overdue">
              <span>Overdue</span>
              <strong>{myTasks.overdue}</strong>
            </div>
            <div className="is-today">
              <span>Due Today</span>
              <strong>{myTasks.today}</strong>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="capa-premium__card capa-premium__compact-card">
          <div className="capa-premium__card-head">Alerts / Compliance</div>
          <div className="capa-premium__alert-list">
            <div>
              <i className="fas fa-exclamation-triangle" />
              <span>2 CAPA overdue &gt; 7 days</span>
            </div>
            <div>
              <i className="fas fa-exclamation-circle" />
              <span>1 CAPA stuck in RCA</span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="capa-premium__card capa-premium__compact-card capa-premium__placeholder-card">
          <div className="capa-premium__card-head">Future Card</div>
          <div className="capa-premium__placeholder-body">
            <i className="far fa-plus-square" />
            <span>Data will be added later</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const CapaDashboard = () => {
  const navigate = useNavigate();
  const { userData, canViewCapa } = useCapaAccess();
  const [data, setData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(DASHBOARD_FILTER_INITIAL);
  const [draftFilters, setDraftFilters] = useState(DASHBOARD_FILTER_INITIAL);

  const loadDashboard = useMemo(
    () => async () => {
      const dashboardData = await fetchCapaDashboard(userData);
      setData(dashboardData);
    },
    [userData],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const dashboardData = await fetchCapaDashboard(userData);
      if (!cancelled) setData(dashboardData);
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const taskRows = useMemo(() => {
    const rows = data?.dashboardTaskRows || data?.overdueTaskRows || [];
    return rows;
  }, [data]);

  const assigneeOptions = useMemo(
    () =>
      Array.from(new Set(taskRows.map((row) => String(row.assigneeName || "").trim()).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [taskRows],
  );

  const filteredTaskRows = useMemo(() => {
    return taskRows.filter((row) => {
      const status = statusVariant(row.dueSchedule);
      const assignee = String(row.assigneeName || "").trim();

      if (appliedFilters.statuses.length > 0) {
        const statusTitle = status === "completed" ? "Completed" : status === "overdue" ? "Overdue" : "Upcoming";
        if (!appliedFilters.statuses.includes(statusTitle)) return false;
      }

      if (appliedFilters.assignees.length > 0 && !appliedFilters.assignees.includes(assignee)) {
        return false;
      }

      return true;
    });
  }, [taskRows, appliedFilters]);

  const stageRows = useMemo(() => resolveStageCounts(data), [data]);

  const myTasks = useMemo(() => {
    const pending = filteredTaskRows.filter((row) => statusVariant(row.dueSchedule) === "upcoming").length;
    const overdue = filteredTaskRows.filter((row) => statusVariant(row.dueSchedule) === "overdue").length;
    const today = filteredTaskRows.filter((row) => {
      const dt = new Date(row.overdueDate);
      if (Number.isNaN(dt.getTime())) return false;
      return dt.toDateString() === new Date().toDateString();
    }).length;
    return { pending, overdue, today };
  }, [filteredTaskRows]);

  const activeFilterCount = useMemo(
    () =>
      [
        appliedFilters.dateRange !== DASHBOARD_FILTER_INITIAL.dateRange,
        appliedFilters.statuses.length > 0,
        appliedFilters.assignees.length > 0,
      ].filter(Boolean).length,
    [appliedFilters],
  );

  const donutSeries = [
    data?.overdueCount ?? myTasks.overdue,
    Math.max((data?.openCount ?? filteredTaskRows.length) - (data?.overdueCount ?? myTasks.overdue), 0),
  ];

  const trend = useMemo(
    () =>
      data?.trend30Days || {
        labels: Array.from({ length: 30 }, (_, i) => `D${i + 1}`),
        created: Array.from({ length: 30 }, () => 0),
        closed: Array.from({ length: 30 }, () => 0),
      },
    [data],
  );

  const kpis = [
    { label: "Open CAPA", value: data?.openCount ?? 0, icon: "fa-regular fa-folder-open", tone: "neutral", subtext: "Active workflows" },
    { label: "Overdue CAPA", value: data?.overdueCount ?? 0, icon: "fa-solid fa-triangle-exclamation", tone: "danger", subtext: "Need immediate action" },
    { label: "Closed CAPA", value: data?.closedCount ?? 0, icon: "fa-solid fa-circle-check", tone: "success", subtext: "This quarter" },
    { label: "My Tasks", value: filteredTaskRows.length, icon: "fa-regular fa-user", tone: "info", subtext: "Assigned to me" },
    { label: "Avg Closure Time", value: "12.4d", icon: "fa-regular fa-clock", tone: "purple", subtext: "Rolling 90 days" },
  ];

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canViewCapa) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page capa-premium container-fluid p-0">
      <DashboardHeader
        onRefresh={loadDashboard}
        onOpenFilter={() => {
          setDraftFilters(appliedFilters);
          setFilterOpen(true);
        }}
        onClearFilters={() => {
          setDraftFilters(DASHBOARD_FILTER_INITIAL);
          setAppliedFilters(DASHBOARD_FILTER_INITIAL);
        }}
        activeFilterCount={activeFilterCount}
      />

      <Sidemodal isOpen={filterOpen} onClose={() => setFilterOpen(false)} sidewidth="24rem">
        <div className="capa-list-filter-panel d-flex flex-column flex-grow-1 overflow-hidden">
          <div className="capa-list-filter-panel__header">
            <h2 className="h6 mb-0">Filter Dashboard</h2>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedFilters(draftFilters);
              setFilterOpen(false);
            }}
          >
            <div className="capa-list-filter-panel__body">
              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Date Range</h3>
                <select
                  className="form-select form-select-sm"
                  value={draftFilters.dateRange}
                  onChange={(e) =>
                    setDraftFilters((prev) => ({ ...prev, dateRange: e.target.value }))
                  }
                >
                  {filters.dateRange.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </section>

              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Status</h3>
                <div className="d-flex flex-column gap-1">
                  {filters.status
                    .filter((s) => s !== "All Status")
                    .map((status) => (
                      <label key={status} className="d-flex align-items-center gap-2 small mb-0">
                        <input
                          type="checkbox"
                          checked={draftFilters.statuses.includes(status)}
                          onChange={() =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              statuses: prev.statuses.includes(status)
                                ? prev.statuses.filter((item) => item !== status)
                                : [...prev.statuses, status],
                            }))
                          }
                        />
                        {status}
                      </label>
                    ))}
                </div>
              </section>

              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Assignee</h3>
                <div className="d-flex flex-column gap-1 capa-list-filter-owner-list">
                  {assigneeOptions.length ? (
                    assigneeOptions.map((name) => (
                      <label key={name} className="d-flex align-items-center gap-2 small mb-0">
                        <input
                          type="checkbox"
                          checked={draftFilters.assignees.includes(name)}
                          onChange={() =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              assignees: prev.assignees.includes(name)
                                ? prev.assignees.filter((item) => item !== name)
                                : [...prev.assignees, name],
                            }))
                          }
                        />
                        {name}
                      </label>
                    ))
                  ) : (
                    <span className="text-muted small">No assignees available.</span>
                  )}
                </div>
              </section>
            </div>
            <div className="capa-list-filter-panel__footer">
              <div className="ms-auto d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setDraftFilters(DASHBOARD_FILTER_INITIAL);
                    setAppliedFilters(DASHBOARD_FILTER_INITIAL);
                  }}
                >
                  Clear filters
                </button>
                <button type="submit" className="btn btn-primary btn-sm">
                  Apply filters
                </button>
              </div>
            </div>
          </form>
        </div>
      </Sidemodal>

      <div className="row g-3 mt-0 mb-1">
        {kpis.map((kpi) => (
          <div className="col-12 col-sm-6 col-lg-4 col-xl" key={kpi.label}>
            <KpiCard {...kpi} />
          </div>
        ))}
      </div>

      <ChartsSection donutData={donutSeries} trend={trend} />

      <div className="row g-3 mt-1">
        <div className="col-xl-8 d-flex">
          <TaskTable rows={filteredTaskRows} navigate={navigate} />
        </div>
        <div className="col-xl-4 d-flex">
          <StagePanel stages={stageRows} />
        </div>
      </div>

      <div className="mt-3">
        <BottomPanels myTasks={myTasks} />
      </div>
    </div>
  );
};

export default CapaDashboard;
